
import { useState, useEffect } from 'react';
import { supabase } from '../integrations/supabase/client';
import { useAuth } from '../contexts/AuthContext';

interface BeltLevel {
  id: string;
  name: string;
  order_sequence: number;
}

export const usePromotionEligibility = () => {
  const { profile } = useAuth();
  const [attendanceCount, setAttendanceCount] = useState(0);
  const [currentBelt, setCurrentBelt] = useState<BeltLevel | null>(null);
  const [isEligibleForPromotion, setIsEligibleForPromotion] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const getRequiredAttendance = (beltOrderSequence: number) => {
    // 10th Kyu to 4th Kyu (order 1-7): 45 trainings
    if (beltOrderSequence >= 1 && beltOrderSequence <= 7) {
      return 45;
    }
    // 3rd, 2nd, 1st Kyu (order 8-10): 100 trainings
    if (beltOrderSequence >= 8 && beltOrderSequence <= 10) {
      return 100;
    }
    // 1st Dan (order 11): 200 trainings
    if (beltOrderSequence === 11) {
      return 200;
    }
    // 2nd Dan (order 12): 400 trainings
    if (beltOrderSequence === 12) {
      return 400;
    }
    // 3rd Dan (order 13): 600 trainings
    if (beltOrderSequence === 13) {
      return 600;
    }
    return 45; // Default fallback
  };

  useEffect(() => {
    const fetchAttendanceAndBelt = async () => {
      if (!profile?.id || !profile?.belt_level_id) {
        setIsLoading(false);
        return;
      }

      try {
        // Fetch current belt info
        const { data: beltData, error: beltError } = await supabase
          .from('belt_levels')
          .select('id, name, order_sequence')
          .eq('id', profile.belt_level_id)
          .single();

        if (beltError) throw beltError;
        setCurrentBelt(beltData);

        // Fetch attendance count for confirmed attendances
        const { data: attendanceData, error: attendanceError } = await supabase
          .from('session_attendance')
          .select('id')
          .eq('student_id', profile.id)
          .eq('attended', true);

        if (attendanceError) throw attendanceError;

        const totalAttendance = attendanceData?.length || 0;
        setAttendanceCount(totalAttendance);

        // Check if eligible for promotion
        const requiredAttendance = getRequiredAttendance(beltData.order_sequence);
        setIsEligibleForPromotion(totalAttendance >= requiredAttendance);

      } catch (error) {
        console.error('Error fetching attendance and belt data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAttendanceAndBelt();
  }, [profile?.id, profile?.belt_level_id]);

  return {
    attendanceCount,
    currentBelt,
    isEligibleForPromotion,
    isLoading,
    requiredAttendance: currentBelt ? getRequiredAttendance(currentBelt.order_sequence) : 0
  };
};
